// P1-2: the canonical asset counts come from the server, computed once in
// CC_Backend/utils/stats.js and shipped on the collection payload as
// `collection.data.stats`. Nothing in the frontend recomputes them any more —
// three screens each used to derive their own variant, which is why the tiles
// and the exported report disagreed.
//
// Vocabulary (Remediation Plan v2 §3), all scoped to one collection:
//   expected     = matched + unscanned   (the intent list)
//   matched      = on the intent list and collected
//   unscanned    = on the intent list and not collected
//   unexpected   = scanned but not on the intent list
//   totalScanned = everything collected (matched + collected unexpected)
//
// INVARIANT: matched + unscanned === expected

const EMPTY_STATS = {
    expected: 0,
    matched: 0,
    unscanned: 0,
    unexpected: 0,
    totalScanned: 0,
    total: 0,
    byAssetTypeId: {},
};

// The actions/* fetch helpers resolve to `undefined` on a network failure, and
// `data` is null for an unknown collection id — so read defensively and fall
// back to zeros rather than rendering `undefined` in the tiles.
const statsFromCollection = (collection) => collection?.data?.stats ?? EMPTY_STATS;

// The server keys the per-type breakdown by asset_type_id; resolve the names with the asset_types
// list the caller has already loaded.
//
// P1-9 (08/11/2026): this used to iterate `byAssetTypeId`, so a type with nothing collected was
// ABSENT from the report entirely and the order of the rest was V8's numeric-key order (i.e. by
// id). The requirements specify the report's per-type totals against the fourteen supported types,
// so all fourteen are emitted — zeros included — in the order the caller supplies (which is now
// sort_order, since getAssetTypes finally sorts).
//
// A RETIRED type is included only when it actually has a count. `Unknown` holds 1,220 historical
// assets, and a report covering one of them must say so rather than hide it; but a retired type
// with nothing collected has no business padding the list.
//
// Any id that resolves to no known type still gets bucketed rather than dropped, so a count can
// never silently vanish from the total.
const statsByTypeName = (stats, assetTypes, { includeZero = true } = {}) => {
    const byTypeId = stats?.byAssetTypeId ?? {};
    const types = Array.isArray(assetTypes) ? assetTypes : [];
    const counted = new Set();
    const result = {};

    types.forEach((assetType) => {
        const count = Object.entries(byTypeId)
            .filter(([typeId]) => String(typeId) === String(assetType.id))
            .reduce((sum, [typeId, value]) => {
                counted.add(String(typeId));
                return sum + value;
            }, 0);

        const isRetired = assetType.retiredAt != null;
        if (count > 0 || (includeZero && !isRetired)) {
            result[assetType.name] = (result[assetType.name] || 0) + count;
        }
    });

    // Ids the loaded list does not explain — a type deleted from under the data, say. Kept so the
    // per-type figures still add up to totalScanned.
    const orphaned = Object.entries(byTypeId)
        .filter(([typeId]) => !counted.has(String(typeId)))
        .reduce((sum, [, value]) => sum + value, 0);
    if (orphaned > 0) {
        result.Unknown = (result.Unknown || 0) + orphaned;
    }

    return result;
};

// Which of the three mutually exclusive buckets does this row fall in? Mirrors the
// partition in CC_Backend/utils/stats.js exactly — unexpected first, then collected,
// and NEVER a comparison against false, because both flags are nullable.
const bucketOf = (asset) => {
    if (asset == null) return null;
    if (asset.unexpected === true) return 'unexpected';
    if (asset.collected === true) return 'matched';
    return 'unscanned';
};

const clamp = (n) => (n > 0 ? n : 0);

/**
 * Move one asset between buckets locally, without asking the server.
 *
 * Added 08/11/2026. Collecting used to re-GET the whole collection — every asset, two
 * joins — purely to refresh five numbers, and the scan re-entrancy guard was held across
 * that second round trip, so any scan arriving inside the window was silently dropped. On a
 * warehouse connection that happened at any normal scanning pace, and it read to the driver
 * as "sometimes it doesn't collect".
 *
 * This is a FAST PATH, not a second definition of the counts. The server payload still
 * wins whenever one arrives (opening the order, an import, a delete), and P1-2's rule that
 * the counts have exactly one definition is preserved by mirroring the partition here and
 * testing both against the same cases.
 *
 *   before - the row as it was, or null when the asset is newly created
 *   after  - the row as it now is, or null when it has been removed
 */
const applyAssetTransition = (stats, before, after) => {
    const base = stats ?? EMPTY_STATS;
    const from = bucketOf(before);
    const to = bucketOf(after);

    const next = {
        ...base,
        byAssetTypeId: { ...(base.byAssetTypeId ?? {}) }
    };

    if (from === to && before != null && after != null
        && before.asset_type_id === after.asset_type_id) {
        return next;   // nothing that the counts can see actually changed
    }

    if (from != null) next[from] = clamp(next[from] - 1);
    if (to != null) next[to] = clamp(next[to] + 1);

    if (before == null && after != null) next.total = clamp(next.total + 1);
    if (before != null && after == null) next.total = clamp(next.total - 1);

    // totalScanned counts everything collected, matched and unexpected alike.
    const wasCollected = before != null && before.collected === true;
    const isCollected = after != null && after.collected === true;

    if (wasCollected && !isCollected) next.totalScanned = clamp(next.totalScanned - 1);
    if (!wasCollected && isCollected) next.totalScanned = clamp(next.totalScanned + 1);

    // Per-type totals track collected rows only, so follow the same transition.
    if (wasCollected && before.asset_type_id != null) {
        const key = before.asset_type_id;
        next.byAssetTypeId[key] = clamp((next.byAssetTypeId[key] ?? 0) - 1);
        if (next.byAssetTypeId[key] === 0) delete next.byAssetTypeId[key];
    }
    if (isCollected && after.asset_type_id != null) {
        const key = after.asset_type_id;
        next.byAssetTypeId[key] = (next.byAssetTypeId[key] ?? 0) + 1;
    }

    next.expected = next.matched + next.unscanned;

    return next;
};

const exports = {
    EMPTY_STATS,
    statsFromCollection,
    statsByTypeName,
    applyAssetTransition,
};

export default exports;
export {
    EMPTY_STATS,
    statsFromCollection,
    statsByTypeName,
    applyAssetTransition,
};
